import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  CheckCircle, 
  XCircle, 
  User, 
  Calendar, 
  DollarSign, 
  FileText, 
  AlertCircle,
  Eye
} from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../ui/Toast';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface PendingApproval {
  id: string;
  expense_id: string;
  expense_description: string;
  expense_amount: number;
  expense_currency: string;
  expense_date: string;
  expense_category: string | null;
  submitted_by: string;
  submitted_at: string | null;
  approval_level: number;
  created_at: string;
}

interface ApprovalHistory {
  id: string;
  approver_name: string;
  approver_role: string;
  approval_level: number;
  status: string;
  comments: string | null;
  created_at: string;
  approved_at: string | null;
}

interface ExpenseDetails {
  id: string;
  description: string;
  amount: number;
  currency: string;
  expense_date: string;
  category: { name: string } | null;
  user: { name: string };
  status: string;
  receipt_url: string | null;
  merchant: string | null;
  notes: string | null;
}

interface ApprovalModalProps {
  approval: PendingApproval;
  onApprove: (comments?: string) => Promise<void>;
  onReject: (comments: string) => Promise<void>;
  onClose: () => void;
}

export const ApprovalModal: React.FC<ApprovalModalProps> = ({
  approval,
  onApprove,
  onReject,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [expenseDetails, setExpenseDetails] = useState<ExpenseDetails | null>(null);
  const [approvalHistory, setApprovalHistory] = useState<ApprovalHistory[]>([]);
  const [comments, setComments] = useState('');
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const apiClient = useApiClient();
  const { toast } = useToast();

  const fetchExpenseDetails = useCallback(async () => {
    try {
      const data = await apiClient.get<ExpenseDetails>(`/expenses/${approval.expense_id}`);
      setExpenseDetails(data);
    } catch (err) {
      console.error('Failed to fetch expense details:', err);
    }
  }, [apiClient, approval.expense_id]);

  const fetchApprovalHistory = useCallback(async () => {
    try {
      const data = await apiClient.get<ApprovalHistory[]>(`/approvals/history/${approval.expense_id}`);
      setApprovalHistory(data || []);
    } catch (err) {
      console.error('Failed to fetch approval history:', err);
    }
  }, [apiClient, approval.expense_id]);

  useEffect(() => {
    fetchExpenseDetails();
    fetchApprovalHistory();
  }, [fetchExpenseDetails, fetchApprovalHistory]);

  const handleAction = async () => {
    if (!action) return;

    try {
      setLoading(true);
      setInlineError(null);
      
      if (action === 'approve') {
        await onApprove(comments || undefined);
      } else {
        if (!comments.trim()) {
          setInlineError('Comments are required for rejection');
          toast.error('Comments are required for rejection');
          return;
        }
        await onReject(comments);
      }
      
      onClose();
    } catch (err: any) {
      const msg = err?.message || `Failed to ${action} expense`;
      setInlineError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'waiting':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const daysPending = Math.floor((Date.now() - new Date(approval.created_at).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Expense Approval Review
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Level {approval.approval_level} approval • {daysPending} days pending
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            {/* Expense Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Expense Details
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Description</p>
                        <p className="text-gray-900">{approval.expense_description}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <DollarSign className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Amount</p>
                        <p className="text-xl font-semibold text-gray-900">
                          {formatCurrency(approval.expense_amount, approval.expense_currency)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Date</p>
                        <p className="text-gray-900">{formatDate(approval.expense_date)}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <User className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Submitted By</p>
                        <p className="text-gray-900">{approval.submitted_by}</p>
                        <p className="text-sm text-gray-500">
                          {approval.submitted_at ? formatDate(approval.submitted_at) : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {approval.expense_category && (
                      <div className="flex items-start space-x-3">
                        <div className="h-5 w-5 bg-blue-100 rounded mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Category</p>
                          <p className="text-gray-900">{approval.expense_category}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Details */}
                {expenseDetails && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-2">
                      Additional Information
                    </h4>
                    
                    {expenseDetails.merchant && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-gray-500">Merchant</p>
                        <p className="text-gray-900">{expenseDetails.merchant}</p>
                      </div>
                    )}

                    {expenseDetails.notes && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-gray-500">Notes</p>
                        <p className="text-gray-900">{expenseDetails.notes}</p>
                      </div>
                    )}

                    {expenseDetails.receipt_url && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-gray-500">Receipt</p>
                        <button
                          onClick={() => setShowReceiptModal(true)}
                          className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View Receipt</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column - Approval History */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Approval History
                </h3>
                
                {approvalHistory.length === 0 ? (
                  <p className="text-gray-500 text-sm">No approval history available</p>
                ) : (
                  <div className="space-y-3">
                    {approvalHistory.map((history, index) => (
                      <div key={history.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {history.approver_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({history.approver_role})
                            </span>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(history.status)}`}>
                            {history.status}
                          </span>
                        </div>
                        
                        <div className="text-xs text-gray-500 mb-1">
                          Level {history.approval_level} • {formatDate(history.created_at)}
                          {history.approved_at && ` • Completed ${formatDate(history.approved_at)}`}
                        </div>
                        
                        {history.comments && (
                          <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            {history.comments}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Urgency Indicator */}
            {daysPending > 2 && (
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      {daysPending > 7 ? 'Urgent: ' : 'Attention: '}
                      This expense has been pending for {daysPending} days
                    </p>
                    <p className="text-xs text-yellow-700">
                      Consider reviewing and taking action to maintain workflow efficiency
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Take Action
              </h3>

              {inlineError && (
                <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm" role="alert" aria-live="assertive">
                  {inlineError}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4 mb-4">
                <button
                  onClick={() => setAction('approve')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md border ${
                    action === 'approve'
                      ? 'bg-green-50 border-green-300 text-green-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Approve</span>
                </button>
                
                <button
                  onClick={() => setAction('reject')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md border ${
                    action === 'reject'
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <XCircle className="h-4 w-4" />
                  <span>Reject</span>
                </button>
              </div>

              {/* Comments */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments {action === 'reject' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={
                    action === 'approve' 
                      ? 'Optional: Add approval comments...'
                      : action === 'reject'
                      ? 'Required: Explain why this expense is being rejected...'
                      : 'Add comments about your decision...'
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleAction}
                  disabled={loading || !action || (action === 'reject' && !comments.trim())}
                  className={`px-4 py-2 rounded-md text-white font-medium disabled:opacity-50 ${
                    action === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : action === 'reject'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-gray-400'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    `${action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Select Action'} Expense`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && expenseDetails?.receipt_url && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg max-w-3xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium">Receipt</h3>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4">
              <img
                src={expenseDetails.receipt_url}
                alt="Receipt"
                className="max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
